import { useEffect, useState } from 'react';

//import Component
import PageForm from 'components/pageForm/PageForm';
import SubNavbar from '../../components/SubNavbar/SubNavbar';
import StyledInput from '../../components/styledInput/StyledInput';
import { SearchLoop } from '../../assets/icons';
import ApiDatabase from 'server';
import useContext from '../../hooks/useContext';
import EmptyState from '../../components/EmptyState/EmptyState';
import Pagination from '../../components/pagination/pagination';
import IdentityValidationMobileCard from '../../components/card/MobileListCard/IdentityValidationMobileCard';
import useSlideOver from '../../components/SlideOver/useSlideOver';

// Skeleton pour les cartes (responsive)
const CardSkeleton = () => (
  <div className="flex flex-col gap-3 p-4 md:p-6 lg:p-8 animate-pulse">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="h-5 bg-gray-200 rounded w-32"></div>
          <div className="h-6 bg-gray-200 rounded w-12"></div>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="h-4 bg-gray-200 rounded w-40"></div>
          <div className="h-4 bg-gray-200 rounded w-28"></div>
        </div>
      </div>
    ))}
  </div>
);

const IdentityValidation = () => {
  const [tempWorkers, setTempWorkers] = useState([]);
  const [filteredTempWorkers, setFilteredTempWorkers] = useState([]);
  const [search, setSearch] = useState('');
  const { updateTempWorker, setUpdateTempWorker, setObjID } = useContext();
  const { toggleSlideOver } = useSlideOver();
  const token = localStorage.getItem('xsrfToken');
  const [isWaiting, setIsWaiting] = useState(true);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const limitElement = 20;

  // Helper function to check if a temp worker has valid displayable data
  const isValidTempWorker = (tw) => {
    if (!tw) return false;
    
    // Check both nested user object and root level
    const user = tw?.user || tw;
    
    // Get all possible name/contact fields
    const firstname = (user?.firstname || tw?.firstname || '').toString().trim();
    const lastname = (user?.lastname || tw?.lastname || '').toString().trim();
    const email = (user?.email || tw?.email || '').toString().trim();
    const phone = (user?.phone || tw?.phone || '').toString().trim();
    
    // Must have at least a name (firstname or lastname) OR email to be displayable
    const hasName = firstname.length > 0 || lastname.length > 0;
    const hasEmail = email.length > 0;
    
    return hasName || hasEmail;
  };

  // Helper function to check if a temp worker has files to validate (only pending = "1")
  const hasFilesToValidate = (tw) => {
    if (!tw) return false;
    
    const hasPendingValidation = (v) => {
      if (v === undefined || v === null) return false;
      const num = Number(v);
      return !isNaN(num) && num === 1; // Only pending documents
    };
    
    return (
      hasPendingValidation(tw?.identity?.validated) ||
      hasPendingValidation(tw?.selfie?.validated) ||
      hasPendingValidation(tw?.visa?.validated) ||
      hasPendingValidation(tw?.card_vitality?.validated) ||
      hasPendingValidation(tw?.proof_of_address?.validated) ||
      hasPendingValidation(tw?.accommodation_of_address?.validated)
    );
  };

  const getTempWorkers = () => {
    ApiDatabase.getAllUsersWithIdentityFilesToComplete(
      { token: token },
      (data) => {
        // Filter out temp workers with no valid displayable data OR no files to validate
        const validTempWorkers = (data || []).filter(tw => {
          const hasValidData = isValidTempWorker(tw);
          const hasFiles = hasFilesToValidate(tw);
          return hasValidData && hasFiles;
        });
        setTempWorkers(validTempWorkers);
        setIsWaiting(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
        }
      }
    );
  };

  useEffect(() => {
    if (updateTempWorker) {
      ApiDatabase.getUserWithIdentityFilesToComplete(
        { token: token, idTempWorker: updateTempWorker },
        (data) => {
          const updatedData = Array.isArray(data) ? data[0] : data;
          const stillHasPending = hasFilesToValidate(updatedData);
          
          // Update the main list: remove or update the tempWorker
          setTempWorkers(prevList => {
            if (stillHasPending) {
              // Update the item in the list
              return prevList.map(tw => {
                const twId = tw?._id || tw?.user?._id;
                const updatedId = updatedData?._id || updatedData?.user?._id;
                return twId === updatedId ? updatedData : tw;
              });
            } else {
              // Remove the item from the list (no more pending documents)
              return prevList.filter(tw => {
                const twId = tw?._id || tw?.user?._id;
                const updatedId = updatedData?._id || updatedData?.user?._id;
                return twId !== updatedId;
              });
            }
          });
          
          setUpdateTempWorker(false);
        },
        (err) => {
          if (err.name !== 'CanceledError') {
            console.error(err);
          }
        }
      );
    }
  }, [updateTempWorker]);

  useEffect(() => {
    getTempWorkers();
  }, []);

  // Filter temp workers based on search
  useEffect(() => {
    if (search.trim() === '') {
      setFilteredTempWorkers(tempWorkers);
    } else {
      const searchLower = search.toLowerCase().trim();
      const filtered = tempWorkers.filter((tw) => {
        const user = tw?.user || tw;
        const firstname = (user?.firstname || tw?.firstname || '').toLowerCase();
        const lastname = (user?.lastname || tw?.lastname || '').toLowerCase();
        const email = (user?.email || tw?.email || '').toLowerCase();
        const phone = (user?.phone || tw?.phone || '').toLowerCase();
        const fullName = `${firstname} ${lastname}`;
        
        return (
          firstname.includes(searchLower) ||
          lastname.includes(searchLower) ||
          email.includes(searchLower) ||
          phone.includes(searchLower) ||
          fullName.includes(searchLower)
        );
      });
      setFilteredTempWorkers(filtered);
    }
    // Reset to first page when search changes
    setCurrentPage(1);
  }, [search, tempWorkers]);

  // Helper function to count documents "à vérifier" (pending = status 1) for a temp worker
  const countDocuments = (tw) => {
    if (!tw) return 0;
    const isPending = (v) => {
      if (v === undefined || v === null) return false;
      const num = Number(v);
      return !isNaN(num) && num === 1; // Only pending documents (à vérifier)
    };
    
    let count = 0;
    if (isPending(tw?.identity?.validated)) count++;
    if (isPending(tw?.selfie?.validated)) count++;
    if (isPending(tw?.visa?.validated)) count++;
    if (isPending(tw?.card_vitality?.validated)) count++;
    if (isPending(tw?.proof_of_address?.validated)) count++;
    if (isPending(tw?.accommodation_of_address?.validated)) count++;
    return count;
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredTempWorkers.length / limitElement);
  const countTotal = filteredTempWorkers.length;
  const startIndex = (currentPage - 1) * limitElement;
  const endIndex = startIndex + limitElement;
  const paginatedTempWorkers = filteredTempWorkers.slice(startIndex, endIndex);
  const countPerPage = paginatedTempWorkers.length;
  
  // Calculate total documents for paginated temp workers
  const totalDocuments = paginatedTempWorkers.reduce((sum, tw) => sum + countDocuments(tw), 0);
  
  // Custom pagination text
  const paginationText = `Affichage de ${countPerPage} intérimaire${countPerPage > 1 ? 's' : ''} (${totalDocuments} document${totalDocuments > 1 ? 's' : ''} à vérifier)`;

  // Function to open temp worker slideover
  const openTempWorkerInfo = (tw) => {
    const userId = tw?.id_user || tw?.user?._id || tw?.user?.id_user || tw?._id;
    if (userId) {
      setObjID(userId);
      toggleSlideOver('get_temp_worker');
    }
  };

  // Helper to count accepted files for a temp worker
  const countAcceptedFiles = (tw) => {
    if (!tw) return { accepted: 0, total: 0 };
    
    const hasValidation = (v) => {
      if (v === undefined || v === null) return false;
      const num = Number(v);
      return !isNaN(num) && num >= 1;
    };
    
    const isAccepted = (v) => {
      const n = parseInt(v);
      return n === 2 || n === 3;
    };
    
    let total = 0;
    let accepted = 0;
    
    if (hasValidation(tw?.identity?.validated)) {
      total++;
      if (isAccepted(tw?.identity?.validated)) accepted++;
    }
    if (hasValidation(tw?.selfie?.validated)) {
      total++;
      if (isAccepted(tw?.selfie?.validated)) accepted++;
    }
    if (hasValidation(tw?.visa?.validated)) {
      total++;
      if (isAccepted(tw?.visa?.validated)) accepted++;
    }
    if (hasValidation(tw?.card_vitality?.validated)) {
      total++;
      if (isAccepted(tw?.card_vitality?.validated)) accepted++;
    }
    if (hasValidation(tw?.proof_of_address?.validated)) {
      total++;
      if (isAccepted(tw?.proof_of_address?.validated)) accepted++;
    }
    if (hasValidation(tw?.accommodation_of_address?.validated)) {
      total++;
      if (isAccepted(tw?.accommodation_of_address?.validated)) accepted++;
    }
    
    return { accepted, total };
  };

  return (
    <>
      <PageForm>
        <>
          <SubNavbar>
            <div className="flex justify-between items-center w-full gap-3">
              <div className="flex gap-2 items-center flex-1 w-full tab:flex-1 tab:max-w-none">
                <div className="flex-1 w-full tab:max-w-[400px]">
                  <StyledInput
                    type="text"
                    placeholder="Rechercher..."
                    imgFront={<SearchLoop wh={16} color="#6B7280" />}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </SubNavbar>
          <div className="flex flex-col w-full h-full overflow-hidden">
            <div className="flex-1 overflow-y-auto">
              {isWaiting ? (
                <CardSkeleton />
              ) : (
                <>
                  {paginatedTempWorkers.length > 0 ? (
                    <div className="flex flex-col gap-3 p-4 md:p-6 lg:p-6">
                      {paginatedTempWorkers.map((tempWorkerInfos, index) => {
                        const counts = countAcceptedFiles(tempWorkerInfos);
                        return (
                          <IdentityValidationMobileCard
                            key={tempWorkerInfos?._id || tempWorkerInfos?.user?._id || index}
                            tempWorker={tempWorkerInfos}
                            nbFiles={counts.accepted}
                            nbFilesTotal={counts.total}
                            onOpenTempWorker={openTempWorkerInfo}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <EmptyState type="identity_validation" />
                  )}
                </>
              )}
            </div>
          </div>

          {/* Pagination footer - outside overflow container like Company.js */}
          {!isWaiting && totalPages >= 1 && (
            <div className="flex-shrink-0 border-t border-gray-100 bg-white px-4 md:px-8 py-4">
              <Pagination
                currentPage={currentPage}
                countPerPage={countPerPage}
                countTotal={countTotal}
                totalPages={totalPages}
                setCurrentPage={setCurrentPage}
                customText={paginationText}
              />
            </div>
          )}
        </>
      </PageForm>
    </>
  );
};

export default IdentityValidation;
